import { createContext } from 'preact';
import { useCallback, useContext, useEffect, useReducer } from 'preact/hooks';
import merge from 'deepmerge';
import {
	getUrlParam,
	getCookie,
	getJsFromString,
	removeEmptyFromNestedObject,
	getPageUrl,
	getPageSearch
} from '@/utils';

const translationTests = {
	js_var: (x, y) => getJsFromString(x),
	url_location: (_, translations) =>
		Object.keys(translations).filter(
			translationKey => getPageUrl().indexOf(translationKey) > -1
		)[0],
	url_query: x => getUrlParam(x, getPageSearch()),
	cookie: x => getCookie(x)
};

function whichTranslation({
	languageTest = {},
	useTranslation,
	useToggle,
	...translations
} = {}) {
	if (!useTranslation) {
		return;
	}

	if (
		languageTest.type === 'cookie' &&
		translationTests[languageTest.type](languageTest.param)?.charAt(0) === '?'
	) {
		return Object.keys(translations).filter(
			cookieKey =>
				translationTests[languageTest.type](languageTest.param).indexOf(cookieKey) > -1
		)[0];
	}

	return translationTests[languageTest.type](languageTest.param, translations);
}

function getTranslatedConfig({ config, value, initialConfig }) {
	const { translation } = config.properties;

	if (!translation?.useTranslation) {
		return initialConfig;
	}

	if (value === 'default') {
		return initialConfig;
	}

	if (translation[value]) {
		return merge.all([
			{},
			config,
			removeEmptyFromNestedObject(translation[value]),
			{ translationSet: value }
		]);
	}

	return initialConfig;
}

function getInitialState(config = {}) {
	const lang = whichTranslation(config.properties?.translation ?? {});
	if (lang) {
		return getTranslatedConfig({
			config,
			initialConfig: config,
			value: lang
		});
	}
	return config;
}

function reducer(state, action) {
	switch (action.type) {
		case 'set_language':
			const translatedConfig = getTranslatedConfig({
				config: state,
				value: action.payload.value,
				initialConfig: action.payload.initialConfig
			});

			if (translatedConfig) {
				return {
					...translatedConfig,
					translationSet: action.payload.value
				};
			}
			return {
				...state,
				translationSet: null
			};

		default:
			return {
				...state,
				translationSet: null
			};
	}
}

function useCreateConfigStore(initialConfig = {}, extra = {}) {
	const [state, dispatch] = useReducer(reducer, getInitialState(initialConfig));

	const setLanguage = useCallback(
		lang => {
			dispatch({
				type: 'set_language',
				payload: {
					value: lang,
					initialConfig
				}
			});
		},
		[initialConfig]
	);

	//handle programmtically modified config
	useEffect(() => {
		if (extra.language && extra.language !== state.translationSet) {
			setLanguage(extra.language);
		}
	}, [extra.language, state.translationSet, setLanguage]);

	if (extra.config) {
		return {
			...merge(state, extra.config),
			actions: {
				setLanguage
			}
		};
	}

	return {
		...state,
		actions: {
			setLanguage
		}
	};
}

const ConfigContext = createContext({});

function ConfigStoreProvider({
	children,
	initialConfig,
	form = {},
	multipleIndex,
	...props
}) {
	const configStore = useCreateConfigStore(initialConfig, form.extra);
	return (
		<ConfigContext.Provider
			value={{
				configStore,
				formProps: {
					...form,
					multipleIndex
				}
			}}
		>
			{children}
		</ConfigContext.Provider>
	);
}

function useConfigContext() {
	return useContext(ConfigContext);
}

function useConfigStore() {
	const { configStore } = useConfigContext();
	return configStore;
}

function useFormProps() {
	const { formProps } = useConfigContext();
	return formProps;
}

function useProperties() {
	const config = useConfigStore();
	return config.properties ?? {};
}

function useAdvanced() {
	const properties = useProperties();
	return properties.advanced ?? {};
}

export {
	ConfigContext,
	ConfigStoreProvider,
	useConfigStore,
	useFormProps,
	useProperties,
	useAdvanced
};
